import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LangSrcService } from '../../services/lang-src/lang-src.service';
import { AuthService } from '../../services/auth/auth.service';
import { UserService } from '../../services/user/user.service';
import { WEFIT_PAGES } from '../../services/sso/wefit-sso.service';
import { WefitNavigationService } from '../../services/wefit/wefit-navigation.service';
import { ClickOutsideDirective } from '../../directives/click-outside.directive';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AccessibilityModalComponent } from '../accessibility-modal/accessibility-modal.component';
import { StorageMap } from '@ngx-pwa/local-storage';

interface NavItem {
  label: string;
  route: string[];
  icon: string;
}

@Component({
  standalone: true,
  selector: 'app-page-header',
  imports: [CommonModule, RouterModule, ClickOutsideDirective, TranslateModule],
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss']
})
export class PageHeaderComponent implements OnInit, OnDestroy {

  currentPageLabel = 'Dashboard';
  mobileDropdownOpen = false;
  profileDropdownOpen = false;
  userName = '';
  isDys = false;
  isBigFont = false;
  readonly wefitPages = WEFIT_PAGES;

  private routerSub: Subscription;

  readonly navItems: NavItem[] = [
    { label: this.translate.instant('Tableau de bord'), route: ['learning', 'dashboard'],  icon: 'fa-solid fa-gauge' },
    { label: this.translate.instant('Parcours'),       route: ['games'],                  icon: 'fa-solid fa-route' },
    { label: this.translate.instant('Bibliothèque'),   route: ['learning', 'library'],    icon: 'fa-solid fa-book' },
  ];

  constructor(
    public langSrcSrv: LangSrcService,
    private router: Router,
    private authSrv: AuthService,
    private userSrv: UserService,
    private wefitNavSrv: WefitNavigationService,
    private modal: NgbModal,
    private translate: TranslateService,
    private storage: StorageMap,
  ) {}

  async ngOnInit() {
    const user = await this.authSrv.getUser();
    this.isDys = user?.isDys ?? false;
    this.isBigFont = user?.isBigFont ?? false;
    this.userName = [user?.firstname, user?.lastname].filter(Boolean).join(' ');
    this.detectCurrentPage(this.router.url);

    this.routerSub = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        this.detectCurrentPage(e.urlAfterRedirects);
        this.mobileDropdownOpen = false;
      });
  }

  ngOnDestroy() {
    this.routerSub?.unsubscribe();
  }

  trackByIndex(index: number) { return index; }

  detectCurrentPage(url: string) {
    // /cefrl-exam (examens de fin de palier) fait partie du parcours
    if (url.includes('/games') || url.includes('/cefrl-exam')) this.currentPageLabel = this.translate.instant('Parcours');
    else if (url.includes('/learning/library'))    this.currentPageLabel = this.translate.instant('Bibliothèque');
    else if (url.includes('/learning/lessons'))    this.currentPageLabel = this.translate.instant('Leçons');
    else if (url.includes('/learning/cultures'))   this.currentPageLabel = this.translate.instant('Point culture');
    else if (url.includes('/learning/dictionary')) this.currentPageLabel = this.translate.instant('Dictionnaire');
    else if (url.includes('/learning/stats'))      this.currentPageLabel = this.translate.instant('Statistiques');
    else if (url.includes('/learning/revision'))   this.currentPageLabel = this.translate.instant('Révision');
    else if (url.includes('/learning'))            this.currentPageLabel = this.translate.instant('Tableau de bord');
    else                                           this.currentPageLabel = this.translate.instant('Tableau de bord');
  }

  isActiveItem(item: NavItem): boolean {
    const url = this.router.url;
    if (item.route[0] === 'games') return url.includes('/games') || url.includes('/cefrl-exam');
    // La bibliothèque regroupe leçons, points culture et dictionnaire
    if (item.route[1] === 'library') {
      return ['/learning/library', '/learning/lessons', '/learning/cultures', '/learning/dictionary']
        .some(path => url.includes(path));
    }
    // Les statistiques sont rattachées au tableau de bord
    if (item.route[1] === 'dashboard') return url.includes('/learning/dashboard') || url.includes('/learning/stats');
    return url.includes('/' + item.route.join('/'));
  }

  navigateTo(item: NavItem) {
    const lang = this.langSrcSrv.current?.shortCode;
    this.router.navigate(['/', lang, ...item.route]);
    this.mobileDropdownOpen = false;
  }

  toggleMobileDropdown() {
    this.mobileDropdownOpen = !this.mobileDropdownOpen;
  }

  closeMobileDropdown() {
    this.mobileDropdownOpen = false;
  }

  goToWefitPage(path: string = '') {
    this.wefitNavSrv.goToPage(path);
  }

  openAccessibility() {
    const ref = this.modal.open(AccessibilityModalComponent, { centered: true, size: 'sm' });
    ref.componentInstance.isDys = this.isDys;
    ref.componentInstance.isBigFont = this.isBigFont;
    ref.dismissed.subscribe(() => {
      this.isDys = document.body.classList.contains('dys');
      this.isBigFont = document.body.classList.contains('big-font');
    });
  }

  async logout() {
    this.profileDropdownOpen = false;
    await this.authSrv.logout();
  }

  toggleProfileDropdown() {
    this.profileDropdownOpen = !this.profileDropdownOpen;
  }

  closeProfileDropdown() {
    this.profileDropdownOpen = false;
  }

  goToProfile() {
    this.profileDropdownOpen = false;
    this.goToWefitPage(this.wefitPages.profile);
  }

  // Langues d'interface dans l'ordre Wefit (fr, en, es, de, it) puis les autres
  get langSrcList(): any[] {
    const order = ['fr', 'en', 'es', 'de', 'it'];
    const rank = (l: any) => {
      const i = order.indexOf(l.shortCode);
      return i === -1 ? order.length : i;
    };
    return [...(this.langSrcSrv.langs ?? [])].sort((a, b) => rank(a) - rank(b));
  }

  async changeLangSrc(lang: any) {
    try {
      await this.userSrv.updateLanguage(lang);
      const user: any = await this.storage.get('user').toPromise();
      if (user) {
        user.langSrc = lang;
        await this.storage.set('user', user).toPromise();
      }
    } catch (e) {
      // continue même si l'API échoue
    }
    this.langSrcSrv.changeLanguage(lang);
  }
}
